import React from 'react'
import styles from './builderComponent.scss'
import { withDependencies, named } from '@wix/thunderbolt-ioc'
import { esmLoaderFactory } from './moduleLoader/esmLoader'
import type { BuilderComponentModule, ModuleEntries, IBuilderComponentLoader, BuilderComponentSiteConfig } from './types'
import { getBuilderWidgetClass, getBuilderComponentInstanceClass } from '@wix/thunderbolt-commons'
import type { ISessionManager } from '@wix/thunderbolt-symbols'
import { SiteFeatureConfigSymbol } from '@wix/thunderbolt-symbols'
import { name } from './symbols'
import { SessionManagerSymbol } from 'feature-session-manager'

const WrapBuilderComponent = (Comp: React.ComponentType<any>, builderComponentId: string) => (props: any) => {
	const { _wrapperProps, ...rest } = props
	return React.createElement(
		'div',
		{
			className: `${styles.builderComponentWrapper} ${getBuilderWidgetClass(
				builderComponentId
			)} ${getBuilderComponentInstanceClass(_wrapperProps.componentId)}`,
		},
		React.createElement(Comp, rest)
	)
}

export const builderComponentLoader = withDependencies(
	[named(SiteFeatureConfigSymbol, name), SessionManagerSymbol],
	(
		{ builderComponentsData }: BuilderComponentSiteConfig,
		sessionManager: ISessionManager
	): IBuilderComponentLoader => {
		const getEsmLoader = ({ withExternals }: { withExternals?: boolean }) => {
			const { esm: packageUrls } = builderComponentsData.publicPackagesUrls
			return esmLoaderFactory({
				scriptsCache: {},
				withFetchRetry: false,
				loaderType: 'esm',
				globalThis,
				fetch: () => Promise.resolve(new Response()), // No need to fetch
				...(withExternals && {
					defaultDependencies: {
						react: 'window.React',
						'@wix/builder-interactions': packageUrls['builder-interactions'],
						'@wix/builder-state': packageUrls['builder-state'],
						'@wix/builder-state-react': packageUrls['builder-state-react'],
						'@wix/builder-velo': packageUrls['builder-velo'],
						'@wix/builder-velo-react': packageUrls['builder-velo-react'],
						'@wix/builder-wix-components': packageUrls['builder-wix-components'],
						'@wix/services-manager-react': packageUrls['services-manager-react'],
						'@wix/builder-element-removal': packageUrls['builder-element-removal'],
						// TODO remove when we stop using React 16
						'use-sync-external-store/shim/index.js':
							'https://esm.sh/use-sync-external-store@1.2.0/X-ZXJlYWN0/es2022/shim/index.mjs',
					},
				}),
			})
		}

		const getComponent = async (componentUrl: string, appDefinitionId: string): Promise<BuilderComponentModule> => {
			const moduleLoader = getEsmLoader({ withExternals: true })
			const moduleEntries = await moduleLoader.loadModule<ModuleEntries>(componentUrl)
			const importKey = Object.keys(moduleEntries)[0]
			const component = moduleEntries[importKey]
			if (moduleEntries.injectAccessTokenGetter) {
				const tokenGetter = sessionManager.getBoundAccessTokenFunction(appDefinitionId)
				moduleEntries.injectAccessTokenGetter(tokenGetter)
			}
			return {
				ReactComponent: component,
			}
		}

		const getComponentLoader: IBuilderComponentLoader['getComponentLoader'] = (
			componentUrl,
			builderComponentId,
			appDefinitionId
		) => {
			return async () => {
				const { ReactComponent: component } = await getComponent(componentUrl, appDefinitionId)
				return {
					component: WrapBuilderComponent(component, builderComponentId),
				}
			}
		}

		return {
			getComponentLoader,
		}
	}
)
